import React from 'react';
import { Box, Typography, Slider, Button, Divider } from '@mui/material';
import UniversalModal from './UniversalModal';

const AuditFindPersonDialogs = ({
  imageSourceDialogOpen,
  handleCloseImageSourceDialog,
  findPersonSimilarityThreshold,
  setFindPersonSimilarityThreshold,
  isCameraOn,
  previewImageDataUrl,
  uploadMatchInputRef,
  handleUploadMatchChange,
  primary,
  startCamera,
  stopCamera,
  capturePhotoFromCamera,
  cameraVideoRef,
  handleFindPersonFromPreview,
  uploadMatchLoading,
  unknownConfirmOpen,
  setUnknownConfirmOpen,
  handleStartUnknownSearch,
  unknownMatchRunning,
}) => {
  return (
    <>
      {/* Image Source Selection Dialog */}
      <UniversalModal
        open={imageSourceDialogOpen}
        onClose={handleCloseImageSourceDialog}
        title="Find Person"
        maxWidth="sm"
        fullWidth
        footer={
          <>
            <Button onClick={handleCloseImageSourceDialog} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={handleFindPersonFromPreview}
              variant="contained"
              disabled={!previewImageDataUrl || uploadMatchLoading}
              sx={{
                textTransform: 'none',
                backgroundColor: primary,
                '&:hover': {
                  backgroundColor: primary,
                  opacity: 0.9,
                },
              }}
            >
              {uploadMatchLoading ? 'Finding Person...' : 'Find Person'}
            </Button>
          </>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload an image or capture a photo to search for a person in the audit logs.
          The system first checks whether the image contains exactly one person, then returns both known-person matches and unmatched-log matches on the same page.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  minWidth: 'fit-content',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  lineHeight: '40px',
                }}
              >
                Similarity Threshold: {parseFloat(findPersonSimilarityThreshold || 0).toFixed(2)}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0, maxWidth: 350, mt: 0.5 }}>
                <Slider
                  value={parseFloat(findPersonSimilarityThreshold || 0.3)}
                  onChange={(e, newValue) => setFindPersonSimilarityThreshold(String(newValue))}
                  min={0}
                  max={1}
                  step={0.01}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 0.25, label: '0.25' },
                    { value: 0.5, label: '0.5' },
                    { value: 0.75, label: '0.75' },
                    { value: 1, label: '1' },
                  ]}
                  sx={{
                    width: '100%',
                    '& .MuiSlider-root': {
                      padding: '8px 0 0 0',
                    },
                    '& .MuiSlider-thumb': {
                      width: 10,
                      height: 10,
                      marginTop: '-3px',
                    },
                    '& .MuiSlider-markLabel': {
                      fontSize: '0.65rem',
                      top: 20,
                    },
                    '& .MuiSlider-track': {
                      height: 4,
                      marginTop: '-2px',
                    },
                    '& .MuiSlider-rail': {
                      height: 4,
                      marginTop: '-2px',
                    },
                  }}
                />
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Value between 0.00 and 1.00 (higher = stricter match)
            </Typography>
          </Box>

          {/* Preview */}
          {!isCameraOn && previewImageDataUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src={previewImageDataUrl}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 8 }}
              />
            </Box>
          )}

          {/* Upload / Camera */}
          <Button
            variant="outlined"
            component="label"
            sx={{
              textTransform: 'none',
              borderColor: primary,
              color: primary,
              '&:hover': {
                borderColor: primary,
                backgroundColor: `${primary}10`,
              },
            }}
          >
            Choose Image
            <input hidden accept="image/*" type="file" ref={uploadMatchInputRef} onChange={handleUploadMatchChange} />
          </Button>

          <Divider>or</Divider>

          {/* Camera */}
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                variant={isCameraOn ? 'outlined' : 'contained'}
                onClick={isCameraOn ? stopCamera : startCamera}
                sx={{
                  textTransform: 'none',
                  borderColor: primary,
                  backgroundColor: isCameraOn ? 'transparent' : primary,
                  color: isCameraOn ? primary : 'white',
                  '&:hover': {
                    backgroundColor: isCameraOn ? `${primary}10` : primary,
                    opacity: isCameraOn ? 1 : 0.9,
                  },
                }}
              >
                {isCameraOn ? 'Stop Camera' : 'Start Camera'}
              </Button>
              <Button
                variant="outlined"
                onClick={capturePhotoFromCamera}
                disabled={!isCameraOn}
                sx={{
                  textTransform: 'none',
                  borderColor: primary,
                  color: primary,
                  '&:disabled': {
                    borderColor: 'rgba(0, 0, 0, 0.26)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                Capture
              </Button>
            </Box>
            {isCameraOn && (
              <Box
                sx={{
                  mt: 2,
                  backgroundColor: '#000',
                  border: '3px solid #1976d2',
                  borderRadius: 3,
                  overflow: 'hidden',
                  width: '100%',
                  maxWidth: 400,
                  mx: 'auto',
                }}
              >
                <video
                  ref={cameraVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </UniversalModal>

      {/* Unknown Person confirm dialog */}
      <UniversalModal
        open={unknownConfirmOpen}
        onClose={() => setUnknownConfirmOpen(false)}
        title="Unknown Person"
        maxWidth="xs"
        fullWidth
        footer={
          <>
            <Button onClick={() => setUnknownConfirmOpen(false)}>No</Button>
            <Button onClick={handleStartUnknownSearch} variant="contained" disabled={unknownMatchRunning}>
              Yes
            </Button>
          </>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
          This person does not exist in registered faces. Search in unmatched audit logs?
        </Typography>
      </UniversalModal>
    </>
  );
};

export default AuditFindPersonDialogs;

